import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';

// Products
export const productsService = {
    // Get all products with optional filters
  async getAll(filters = {}, retryCount = 0) {
    try {
      let q = collection(db, 'products');
      
      // Apply filters
      const brandNames = filters.brand
        ? String(filters.brand)
            .split(',')
            .map((name) => name.trim())
            .filter(Boolean)
        : [];

      if (brandNames.length === 1) {
        q = query(q, where('brandName', '==', brandNames[0]));
      }
      if (filters.category) {
        q = query(q, where('categoryName', '==', filters.category));
      }
      if (filters.pharmaOnly) {
        q = query(q, where('isPharma', '==', true));
      }
      if (filters.minPrice) {
        const minPriceNum = parseFloat(filters.minPrice);
        q = query(q, where('offerPrice', '>=', minPriceNum));
      }
      if (filters.maxPrice) {
        const maxPriceNum = parseFloat(filters.maxPrice);
        q = query(q, where('offerPrice', '<=', maxPriceNum));
      }
      
      // Apply sorting - for price filters, don't use complex sorting to avoid index issues
      if (filters.minPrice || filters.maxPrice) {
        // For price filters, don't add sorting to avoid complex index requirements
      } else if (brandNames.length > 0 || filters.category || filters.pharmaOnly) {
        // When filtering by brand/category/pharma, sort by createdAt
        q = query(q, orderBy('createdAt', 'desc'));
      } else {
        // When no filters, use the requested sort field
        const sortField = filters.sortBy || 'createdAt';
        const sortOrder = filters.sortOrder || 'desc';
        q = query(q, orderBy(sortField, sortOrder));
      }
      
      const querySnapshot = await getDocs(q);
      const products = [];
      querySnapshot.forEach((doc) => {
        products.push({ id: doc.id, ...doc.data() });
      });

      let filteredProducts = products;

      if (brandNames.length > 1) {
        filteredProducts = filteredProducts.filter((product) =>
          brandNames.includes(product.brandName)
        );
      }

      if (filters.lifestyleOnly) {
        filteredProducts = filteredProducts.filter((product) => !product.isPharma);
      }

      if (brandNames.length > 1 || filters.lifestyleOnly) {
        return filteredProducts;
      }
      
      // If no products found and we have price filters, try client-side filtering as fallback
      if (products.length === 0 && (filters.minPrice || filters.maxPrice)) {
        // Get all products directly from Firestore without any filters to avoid infinite loop
        const allProductsQuery = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const allProductsSnapshot = await getDocs(allProductsQuery);
        const allProducts = [];
        allProductsSnapshot.forEach((doc) => {
          allProducts.push({ id: doc.id, ...doc.data() });
        });
        
        const filteredProducts = allProducts.filter(product => {
          const offerPrice = parseFloat(product.offerPrice) || 0;
          const minPrice = filters.minPrice ? parseFloat(filters.minPrice) : 0;
          const maxPrice = filters.maxPrice ? parseFloat(filters.maxPrice) : Infinity;
          return offerPrice >= minPrice && offerPrice <= maxPrice;
        });
        return filteredProducts;
      }
      
      return products;
    } catch (error) {
      console.error('Error getting products:', error);
      
      // Handle specific Firestore errors
      if (error.code === 'permission-denied') {
        console.warn('Permission denied - check Firestore rules');
        return [];
      }
      
      if (error.code === 'unavailable') {
        console.warn('Firestore temporarily unavailable - retrying...');
        // You could implement retry logic here
        return [];
      }
      
      if (error.message && error.message.includes('400')) {
        console.warn('Bad request error - possible network issue');
        
        // Retry logic for network errors
        if (retryCount < 3) {
          console.log(`Retrying... Attempt ${retryCount + 1}/3`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return this.getAll(filters, retryCount + 1);
        }
        
        return [];
      }
      
      throw error;
    }
  },

  // Get product by ID
  async getById(id) {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error getting product:', error);
      throw error;
    }
  },

  // Add new product
  async add(productData) {
    try {
      const docRef = await addDoc(collection(db, 'products'), {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      });
      return { id: docRef.id, ...productData };
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  },

  // Update product
  async update(id, productData) {
    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        ...productData,
        updatedAt: serverTimestamp()
      });
      return { id, ...productData };
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Delete product
  async delete(id) {
    try {
      await deleteDoc(doc(db, 'products', id));
      return { id };
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // Toggle product status
  async toggleStatus(id, isActive) {
    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        isActive,
        updatedAt: serverTimestamp()
      });
      return { id, isActive };
    } catch (error) {
      console.error('Error toggling product status:', error);
      throw error;
    }
  }
};

// Categories
export const categoriesService = {
  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categories = [];
      querySnapshot.forEach((doc) => {
        categories.push({ id: doc.id, ...doc.data() });
      });
      return categories;
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  },

  async add(categoryData) {
    try {
      const docRef = await addDoc(collection(db, 'categories'), {
        ...categoryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...categoryData };
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  },

  async update(id, categoryData) {
    try {
      const docRef = doc(db, 'categories', id);
      await updateDoc(docRef, {
        ...categoryData,
        updatedAt: serverTimestamp()
      });
      return { id, ...categoryData };
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, 'categories', id));
      return { id };
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
};

// Business Types
export const businessTypesService = {
  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, 'businessTypes'));
      const businessTypes = [];
      querySnapshot.forEach((doc) => {
        businessTypes.push({ id: doc.id, ...doc.data() });
      });
      return businessTypes;
    } catch (error) {
      console.error('Error getting business types:', error);
      throw error;
    }
  },

  async add(businessTypeData) {
    try {
      const docRef = await addDoc(collection(db, 'businessTypes'), {
        ...businessTypeData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...businessTypeData };
    } catch (error) {
      console.error('Error adding business type:', error);
      throw error;
    }
  },

  async update(id, businessTypeData) {
    try {
      const docRef = doc(db, 'businessTypes', id);
      await updateDoc(docRef, {
        ...businessTypeData,
        updatedAt: serverTimestamp()
      });
      return { id, ...businessTypeData };
    } catch (error) {
      console.error('Error updating business type:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, 'businessTypes', id));
      return { id };
    } catch (error) {
      console.error('Error deleting business type:', error);
      throw error;
    }
  }
};

// Brands
export const brandsService = {
  async getAll(activeOnly = true) {
    try {
      let q = collection(db, 'brands');
      
      const querySnapshot = await getDocs(q);
      const brands = [];
      
      querySnapshot.forEach((doc) => {
        const brandData = { id: doc.id, ...doc.data() };
        brands.push(brandData);
      });
      
      // For admin dashboard, return all brands regardless of activeOnly parameter
      if (!activeOnly) {
        return brands;
      }
      
      // If activeOnly is true, filter by isActive status
      const activeBrands = brands.filter(brand => brand.isActive !== false);
      return activeBrands;
    } catch (error) {
      console.error('Error getting brands:', error);
      throw error;
    }
  },

  async add(brandData) {
    try {
      const docRef = await addDoc(collection(db, 'brands'), {
        ...brandData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { id: docRef.id, ...brandData };
    } catch (error) {
      console.error('Error adding brand:', error);
      throw error;
    }
  },

  async update(id, brandData) {
    try {
      const docRef = doc(db, 'brands', id);
      await updateDoc(docRef, {
        ...brandData,
        updatedAt: serverTimestamp()
      });
      return { id, ...brandData };
    } catch (error) {
      console.error('Error updating brand:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, 'brands', id));
      return { id };
    } catch (error) {
      console.error('Error deleting brand:', error);
      throw error;
    }
  }
};

// Advertisements
export const advertisementsService = {
  async getAll() {
    try {
      const querySnapshot = await getDocs(collection(db, 'advertisements'));
      const advertisements = [];
      querySnapshot.forEach((doc) => {
        advertisements.push({ id: doc.id, ...doc.data() });
      });
      return advertisements;
    } catch (error) {
      console.error('Error getting advertisements:', error);
      throw error;
    }
  },

  async add(adData) {
    try {
      const docRef = await addDoc(collection(db, 'advertisements'), {
        ...adData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      });
      return { id: docRef.id, ...adData };
    } catch (error) {
      console.error('Error adding advertisement:', error);
      throw error;
    }
  },

  async update(id, adData) {
    try {
      const docRef = doc(db, 'advertisements', id);
      await updateDoc(docRef, {
        ...adData,
        updatedAt: serverTimestamp()
      });
      return { id, ...adData };
    } catch (error) {
      console.error('Error updating advertisement:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, 'advertisements', id));
      return { id };
    } catch (error) {
      console.error('Error deleting advertisement:', error);
      throw error;
    }
  },

  async toggleStatus(id, isActive) {
    try {
      const docRef = doc(db, 'advertisements', id);
      await updateDoc(docRef, {
        isActive,
        updatedAt: serverTimestamp()
      });
      return { id, isActive };
    } catch (error) {
      console.error('Error toggling advertisement status:', error);
      throw error;
    }
  }
};

// Media/File Upload
export const mediaService = {
  async uploadImage(file, folder = 'products') {
    try {
      const timestamp = Date.now();
      const fileName = `${folder}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: fileName,
        name: file.name,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  },

  async deleteImage(path) {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return { path };
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }
};

// Search
export const searchService = {
  async searchProducts(searchTerm) {
    try {
      // For now, we'll get all products and filter client-side
      // In production, you might want to use Algolia or similar for better search
      const products = await productsService.getAll();
      const filtered = products.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return filtered;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  async getPopularProducts(limitCount = 8) {
    try {
      // Simplified approach to avoid CORB issues
      const allProducts = await productsService.getAll();
      // Sort by creation date if available, otherwise return first products
      const sortedProducts = allProducts.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return 0;
      });
      return sortedProducts.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting popular products:', error);
      return [];
    }
  }
};

// Analytics/Stats
export const analyticsService = {
  async getStats() {
    try {
      const [products, categories, brands, advertisements] = await Promise.all([
        productsService.getAll(),
        categoriesService.getAll(),
        brandsService.getAll(),
        advertisementsService.getAll()
      ]);

      return {
        totalProducts: products.length,
        activeProducts: products.filter(p => p.isActive).length,
        totalCategories: categories.length,
        totalBrands: brands.length,
        totalAdvertisements: advertisements.length,
        activeAdvertisements: advertisements.filter(a => a.isActive).length
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      throw error;
    }
  }
};
